import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-premium';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, Icon, ModuleSearchField, Utils } from 'app/main/components';
import { useModel, useSettings, useUserData } from 'app/main/components/hooks';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';

interface DemsDefaultRecipientAddDialogProps {
	updateRefresh: VoidFunction;
}

export function showDemsDefaultRecipientAddDialog(props: DemsDefaultRecipientAddDialogProps) {
	return openDialog({
		children: <DemsDefaultRecipientAddDialog updateRefresh={props.updateRefresh} />,
		fullWidth: true,
		maxWidth: 'xs',
		onClose: () => {}
	});
}

function DemsDefaultRecipientAddDialog(props: DemsDefaultRecipientAddDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('mails');

	const { model, sendChange, save } = useModel({ baseModel: { mail_dems_default: 1 }, settings, isEdit: true });

	const addRecipient = () => {
		save({
			onSuccess: () => {
				dispatch(Utils.alertSuccessMsg(t, 'ALERT_ADD_SUCCESS'));
				props.updateRefresh();
				dispatch(closeDialog());
			}
		});
	};

	return (
		<>
			<DialogTitle>{t('DEMS_DEFAULT_RECIPIENTS_ADD_TITLE')}</DialogTitle>
			<DialogContent>
				<Form id="dialogAddRecipient" onSubmit={addRecipient}>
					<ModuleSearchField
						name="mail_uid"
						label={t('MODULE_MAILS_NAME_SINGULAR')}
						module="mails"
						uid={model.mail_uid}
						isEdit
						sendChange={sendChange}
					/>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CLOSE')}</Button>
				<Button type="submit" form="dialogAddRecipient">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}

export default function DemsDefaultRecipientsTab() {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const [refresh, setRefresh] = React.useState(false);
	const updateRefresh = () => setRefresh(state => !state);

	function removeDefaultRecipient(row) {
		Utils.executeStored({
			action: 'st_mails_update',
			data: { mail_uid: row.mail_uid, mail_dems_default: 0, mail_user_modified_uid: userData.user_uid },
			dispatch,
			successHandler: () => {
				updateRefresh();
			}
		});
	}

	const columns: GridColDef[] = [
		{
			field: 'mail_title',
			headerName: t('DEMS_RECIPIENTS_COLUMN_MAIL'),
			minWidth: 200,
			flex: 4,
			renderCell: ({ value }) =>
				ViewTableUtils.renderCellWithTooltip({ value: value || t('MODULE_MAILS_NAME_SINGULAR') }),
			hideable: false
		},
		{
			field: 'mail_table_uid',
			headerName: t('DEMS_RECIPIENTS_COLUMN_ITEM'),
			minWidth: 200,
			flex: 4,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({
					value:
						row.mail_table === 'people'
							? `${row.peop_title}${row.comp_title ? ` - ${row.comp_title}` : ''}`
							: row.mail_table === 'companies'
								? row.comp_title
								: row.user_title,
					to: { module: row.mail_table, uid: value }
				}),
			sortable: false
		},
		{
			field: 'actions',
			type: 'actions',
			width: 40,
			getActions: ({ row }) => [
				<GridActionsCellItem
					label={t('VIEW_ITEM_BUTTON_DELETE')}
					icon={<Icon icon="delete" />}
					onClick={() => removeDefaultRecipient(row)}
				/>
			],
			hideable: false
		}
	];

	return (
		<ViewTable
			className="rounded-8"
			module="mails"
			columns={columns}
			defaultFilters={[
				{
					filt_field: 'mail_dems_default',
					filt_clause: 'equals',
					filt_value: 1
				},
				{
					filt_field: 'mail_deleted',
					filt_clause: 'not_equal',
					filt_value: 1
				}
			]}
			refresh={refresh}
			updateRefresh={updateRefresh}
		/>
	);
}
